<template>
  <div class="data-source-monitor">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-satellite-dish mr-3"></i>
          Policy Monitor
        </h1>
        <p class="page-description">
          Monitor Medicaid policy updates from CMS and state agencies. Track State Plan Amendments, 
          Section 1115 Waivers, and other policy changes that may impact your compliance requirements.
        </p>
      </div>
      <div class="header-actions">
        <router-link to="/source-catalog" class="btn btn-outline-primary">
          <i class="fas fa-book-medical mr-2"></i>
          Browse Catalog
        </router-link>
        <button 
          v-if="!hasDataSources"
          @click="createDefaultSources" 
          class="btn btn-primary btn-lg"
          :disabled="creatingDefaults"
        >
          <i class="fas fa-magic mr-2"></i>
          {{ creatingDefaults ? 'Setting Up...' : 'Quick Setup' }}
        </button>
        <button 
          v-else
          @click="showCreateModal = true" 
          class="btn btn-primary"
        >
          <i class="fas fa-plus mr-2"></i>
          Add Source
        </button>
      </div>
    </div>

    <!-- Pilot States Banner -->
    <div class="pilot-banner">
      <div class="pilot-badge">
        <i class="fas fa-flask"></i>
        PILOT
      </div>
      <div class="pilot-info">
        <strong>Currently monitoring:</strong>
        <span class="state-tags">
          <span v-for="state in pilotStates" :key="state.code" class="state-tag">
            {{ state.name }}
          </span>
        </span>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        @click="activeTab = tab.id"
        class="tab-button"
        :class="{ 'active': activeTab === tab.id }"
      >
        <i :class="tab.icon"></i>
        {{ tab.name }}
        <span v-if="tab.count !== undefined" class="tab-count">{{ tab.count }}</span>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      
      <!-- Sources Tab -->
      <div v-if="activeTab === 'sources'" class="sources-tab">
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p>Loading data sources...</p>
        </div>
        
        <div v-else-if="dataSources.length === 0" class="empty-state">
          <i class="fas fa-satellite-dish fa-4x"></i>
          <h3>No Data Sources Configured</h3>
          <p>Set up policy monitoring to track Medicaid updates for your states.</p>
          <button @click="createDefaultSources" class="btn btn-primary" :disabled="creatingDefaults">
            <i class="fas fa-magic mr-2"></i>
            {{ creatingDefaults ? 'Setting Up...' : 'Quick Setup for Pilot States' }}
          </button>
        </div>
        
        <div v-else class="sources-grid">
          <div 
            v-for="source in dataSources" 
            :key="source.id" 
            class="source-card"
            :class="{ 'running': source.status === 'RUNNING', 'error': source.status === 'ERROR' }"
          >
            <div class="source-header">
              <div class="source-icon">
                <i class="fas fa-database"></i>
              </div>
              <div class="source-info">
                <h4>{{ source.name }}</h4>
                <p class="source-description">{{ source.description }}</p>
              </div>
              <div class="source-status" :class="source.status.toLowerCase()">
                <i :class="getStatusIcon(source.status)"></i>
                {{ source.status_display }}
              </div>
            </div>
            
            <div class="source-states">
              <span class="label">Monitoring:</span>
              <div class="state-chips">
                <span 
                  v-for="state in source.effective_states" 
                  :key="state.code" 
                  class="state-chip"
                >
                  {{ state.code }}
                </span>
              </div>
            </div>
            
            <div class="source-stats">
              <div class="stat">
                <span class="stat-value">{{ source.total_items_collected }}</span>
                <span class="stat-label">Total Events</span>
              </div>
              <div class="stat">
                <span class="stat-value">{{ source.new_items_last_run || 0 }}</span>
                <span class="stat-label">New Last Run</span>
              </div>
              <div class="stat">
                <span class="stat-value">{{ formatSchedule(source.schedule) }}</span>
                <span class="stat-label">Schedule</span>
              </div>
            </div>
            
            <div class="source-footer">
              <div class="last-run">
                <i class="fas fa-clock"></i>
                {{ source.last_run_at ? formatDate(source.last_run_at) : 'Never run' }}
              </div>
              <div class="source-actions">
                <button 
                  @click="runScraper(source)" 
                  class="btn btn-sm btn-outline-primary"
                  :disabled="source.status === 'RUNNING' || runningScrapers.includes(source.id)"
                >
                  <i :class="runningScrapers.includes(source.id) ? 'fas fa-spinner fa-spin' : 'fas fa-play'"></i>
                  {{ runningScrapers.includes(source.id) ? 'Running...' : 'Run Now' }}
                </button>
                <button 
                  v-if="source.status === 'ACTIVE'"
                  @click="pauseSource(source)" 
                  class="btn btn-sm btn-outline-secondary"
                >
                  <i class="fas fa-pause"></i>
                </button>
                <button 
                  v-else-if="source.status === 'PAUSED'"
                  @click="resumeSource(source)" 
                  class="btn btn-sm btn-outline-success"
                >
                  <i class="fas fa-play"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Events Tab -->
      <div v-if="activeTab === 'events'" class="events-tab">
        <!-- ... existing content ... -->
      </div>

      <!-- Field Readiness Tab (Real Estate) -->
      <div v-if="activeTab === 'readiness'" class="readiness-tab">
        <div class="readiness-header">
          <div class="readiness-stats">
            <div class="readiness-stat">
              <span class="label">Open Variances</span>
              <span class="value">{{ openVariances.length }}</span>
            </div>
            <div class="readiness-stat">
              <span class="label">Recurring Risks</span>
              <span class="value">{{ recurringRisks.length }}</span>
            </div>
          </div>
          <button @click="runVarianceAudit" class="btn btn-primary" :disabled="auditing">
            <i :class="auditing ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
            Run Variance Audit
          </button>
        </div>

        <div class="readiness-grid">
          <!-- Active Variances from SQL -->
          <div class="readiness-card">
            <div class="card-header">
              <i class="fas fa-exclamation-circle"></i>
              <h3>Active Variances (SOR)</h3>
            </div>
            <div class="card-body">
              <div v-if="loadingReadiness" class="loading-inline">
                <i class="fas fa-spinner fa-spin"></i> Loading...
              </div>
              <div v-else-if="openVariances.length === 0" class="empty-inline">
                No active variances detected in the SOR.
              </div>
              <table v-else class="readiness-table">
                <thead>
                  <tr>
                    <th>Lot</th>
                    <th>Trade</th>
                    <th>Reason</th>
                    <th>Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="v in openVariances" :key="v.id">
                    <td>{{ v.lot }}</td>
                    <td>{{ v.trade }}</td>
                    <td>{{ v.reason }}</td>
                    <td class="impact-cell" :class="v.impact_level.toLowerCase()">{{ v.impact_level }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Recurring Risk Clusters -->
          <div class="readiness-card">
            <div class="card-header">
              <i class="fas fa-layer-group"></i>
              <h3>Recurring Risk Patterns</h3>
            </div>
            <div class="card-body">
              <div v-if="loadingReadiness" class="loading-inline">
                <i class="fas fa-spinner fa-spin"></i> Loading...
              </div>
              <div v-else-if="recurringRisks.length === 0" class="empty-inline">
                No recurring risks clustered yet.
              </div>
              <div v-else class="risk-clusters">
                <div v-for="risk in recurringRisks" :key="risk.id" class="risk-item">
                  <div class="risk-title">
                    <strong>{{ risk.pattern }}</strong>
                    <span class="risk-badge" :class="risk.severity">{{ risk.severity }}</span>
                  </div>
                  <p>{{ risk.description }}</p>
                  <div class="risk-meta">
                    Detected in {{ risk.frequency }} plans
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- By State Tab -->
      <div v-if="activeTab === 'by-state'" class="by-state-tab">
        <div v-if="loadingByState" class="loading-state">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p>Loading state data...</p>
        </div>
        
        <div v-else class="state-panels">
          <div 
            v-for="state in pilotStates" 
            :key="state.code" 
            class="state-panel"
          >
            <div class="state-header">
              <h3>
                <span class="state-code">{{ state.code }}</span>
                {{ state.name }}
              </h3>
              <span class="event-count">
                {{ eventsByState[state.code]?.count || 0 }} events
              </span>
            </div>
            
            <div class="state-events">
              <div 
                v-for="event in (eventsByState[state.code]?.recent || [])" 
                :key="event.id"
                class="mini-event"
                @click="selectedEvent = event"
              >
                <span class="mini-type" :class="event.item_type">{{ event.item_type_display }}</span>
                <span class="mini-title">{{ event.title }}</span>
                <span v-if="event.effective_date" class="mini-date">{{ formatDate(event.effective_date) }}</span>
              </div>
              
              <div v-if="!eventsByState[state.code]?.recent?.length" class="no-events">
                No recent events for {{ state.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Run History Tab -->
      <div v-if="activeTab === 'history'" class="history-tab">
        <div v-if="loadingHistory" class="loading-state">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p>Loading run history...</p>
        </div>
        
        <div v-else-if="runHistory.length === 0" class="empty-state">
          <i class="fas fa-history fa-4x"></i>
          <h3>No Run History</h3>
          <p>Scraper runs will appear here after execution.</p>
        </div>
        
        <div v-else class="history-list">
          <div 
            v-for="run in runHistory" 
            :key="run.id" 
            class="history-item"
            :class="run.status"
          >
            <div class="history-icon">
              <i :class="getRunStatusIcon(run.status)"></i>
            </div>
            <div class="history-content">
              <div class="history-header">
                <strong>{{ run.data_source_name }}</strong>
                <span class="history-time">{{ formatDate(run.started_at) }}</span>
              </div>
              <div class="history-details">
                <span v-if="run.status === 'completed'" class="history-stats">
                  Found {{ run.items_found }} events ({{ run.new_items }} new)
                </span>
                <span v-else-if="run.status === 'failed'" class="history-error">
                  {{ run.error_message }}
                </span>
                <span v-else class="history-running">
                  Running...
                </span>
              </div>
              <div class="history-meta">
                <span class="trigger-type">{{ run.trigger_type }}</span>
                <span v-if="run.triggered_by_name" class="triggered-by">
                  by {{ run.triggered_by_name }}
                </span>
                <span v-if="run.duration_seconds" class="duration">
                  {{ formatDuration(run.duration_seconds) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Detail Modal -->
    <div v-if="selectedEvent" class="modal-overlay" @click="selectedEvent = null">
      <div class="modal-content event-detail-modal" @click.stop>
        <div class="modal-header">
          <div class="event-badges">
            <span class="badge state-badge">{{ selectedEvent.state }}</span>
            <span class="badge type-badge" :class="selectedEvent.item_type">
              {{ selectedEvent.item_type_display }}
            </span>
            <span v-if="selectedEvent.status" class="badge status-badge" :class="selectedEvent.status">
              {{ selectedEvent.status }}
            </span>
          </div>
          <button @click="selectedEvent = null" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <h2 class="event-detail-title">{{ selectedEvent.title }}</h2>
          
          <div v-if="selectedEvent.effective_date" class="detail-row">
            <label>Effective Date:</label>
            <span>{{ formatDate(selectedEvent.effective_date) }}</span>
          </div>
          
          <div v-if="selectedEvent.what_changed" class="detail-section">
            <h4>What Changed</h4>
            <p>{{ selectedEvent.what_changed }}</p>
          </div>
          
          <div v-if="selectedEvent.why_providers_care" class="detail-section highlight">
            <h4><i class="fas fa-exclamation-circle"></i> Why Providers Care</h4>
            <p>{{ selectedEvent.why_providers_care }}</p>
          </div>
          
          <div v-if="selectedEvent.policy_after" class="detail-section">
            <h4>Current Policy</h4>
            <p>{{ selectedEvent.policy_after }}</p>
          </div>
          
          <div v-if="selectedEvent.policy_before" class="detail-section">
            <h4>Previous Policy</h4>
            <p>{{ selectedEvent.policy_before }}</p>
          </div>
          
          <div v-if="selectedEvent.impact_summary" class="detail-section">
            <h4>Impact Assessment</h4>
            <p>{{ selectedEvent.impact_summary }}</p>
          </div>
          
          <div class="detail-actions">
            <a :href="selectedEvent.source_url" target="_blank" class="btn btn-primary">
              <i class="fas fa-external-link-alt mr-2"></i>
              View Source Document
            </a>
            <button 
              v-if="!selectedEvent.linked_to_analysis"
              @click="linkToAnalysis(selectedEvent)"
              class="btn btn-outline-secondary"
            >
              <i class="fas fa-link mr-2"></i>
              Link to Analysis
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useToast } from 'vue-toastification'
import { debounce } from 'lodash-es'

export default {
  name: 'DataSourceMonitor',
  
  setup() {
    const toast = useToast()
    
    // State
    const loading = ref(true)
    const loadingEvents = ref(false)
    const loadingByState = ref(false)
    const loadingHistory = ref(false)
    const creatingDefaults = ref(false)
    
    const dataSources = ref([])
    const policyEvents = ref([])
    const eventsByState = ref({})
    const runHistory = ref([])
    const runningScrapers = ref([])
    
    const openVariances = ref([])
    const recurringRisks = ref([])
    const loadingReadiness = ref(false)
    const auditing = ref(false)
    
    const selectedEvent = ref(null)
    const showCreateModal = ref(false)
    
    const activeTab = ref('sources')
    
    const pilotStates = ref([
      { code: 'CO', name: 'Colorado' },
      { code: 'AZ', name: 'Arizona' },
      { code: 'NV', name: 'Nevada' }
    ])
    
    const eventFilters = reactive({
      state: '',
      item_type: '',
      search: '',
      days: 90
    })
    
    // Computed
    const tabs = computed(() => [
      { id: 'sources', name: 'Data Sources', icon: 'fas fa-database', count: dataSources.value.length },
      { id: 'events', name: 'Policy & Market Events', icon: 'fas fa-file-alt', count: policyEvents.value.length },
      { id: 'readiness', name: 'Field Readiness', icon: 'fas fa-hard-hat' },
      { id: 'by-state', name: 'By State', icon: 'fas fa-map-marker-alt' },
      { id: 'history', name: 'Run History', icon: 'fas fa-history' }
    ])
    
    const hasDataSources = computed(() => dataSources.value.length > 0)
    
    // Methods
    const loadDataSources = async () => {
      try {
        loading.value = true
        const response = await api.get('/data-sources/')
        dataSources.value = response.data
      } catch (error) {
        console.error('Error loading data sources:', error)
        toast.error('Failed to load data sources')
      } finally {
        loading.value = false
      }
    }
    
    const loadEvents = async () => {
      try {
        loadingEvents.value = true
        const params = { days: eventFilters.days }
        if (eventFilters.state) params.state = eventFilters.state
        if (eventFilters.item_type) params.item_type = eventFilters.item_type
        if (eventFilters.search) params.search = eventFilters.search
        
        const response = await api.get('/policy-events/', { params })
        policyEvents.value = response.data.results || response.data
      } catch (error) {
        console.error('Error loading events:', error)
        toast.error('Failed to load policy events')
      } finally {
        loadingEvents.value = false
      }
    }
    
    const loadEventsByState = async () => {
      try {
        loadingByState.value = true
        const response = await api.get('/policy-events/by_state/')
        eventsByState.value = response.data
      } catch (error) {
        console.error('Error loading events by state:', error)
      } finally {
        loadingByState.value = false
      }
    }
    
    const loadRunHistory = async () => {
      try {
        loadingHistory.value = true
        const response = await api.get('/data-source-runs/')
        runHistory.value = response.data.results || response.data
      } catch (error) {
        console.error('Error loading run history:', error)
      } finally {
        loadingHistory.value = false
      }
    }

    const loadReadinessData = async () => {
      try {
        loadingReadiness.value = true
        // This endpoint would be added to views_real_estate.py
        const response = await api.get('/real-estate/field-readiness-summary/')
        openVariances.value = response.data.open_variances || []
        recurringRisks.value = response.data.recurring_risks || []
      } catch (error) {
        console.error('Error loading readiness data:', error)
        // Mock data for demo
        openVariances.value = [
          { id: 1, lot: '151', trade: 'Framing', reason: 'Missing Egress', impact_level: 'High' },
          { id: 2, lot: '147', trade: 'HVAC', reason: 'Duct Clearance', impact_level: 'Medium' }
        ]
        recurringRisks.value = [
          { id: 1, pattern: 'Plan B Egress', severity: 'critical', frequency: 12, description: 'Bedroom 3 egress clearance consistently fails.' }
        ]
      } finally {
        loadingReadiness.value = false
      }
    }

    const runVarianceAudit = async () => {
      try {
        auditing.value = true
        await api.post('/real-estate/variance-audit-all/')
        toast.success('Variance audit triggered across all active lots.')
        await loadReadinessData()
      } catch (error) {
        toast.error('Failed to trigger variance audit.')
      } finally {
        auditing.value = false
      }
    }
    
    const createDefaultSources = async () => {
      try {
        creatingDefaults.value = true
        await api.post('/data-sources/create-defaults/')
        toast.success('Data sources created successfully!')
        await loadDataSources()
      } catch (error) {
        console.error('Error creating default sources:', error)
        if (error.response?.data?.message) {
          toast.info(error.response.data.message)
        } else {
          toast.error('Failed to create data sources')
        }
      } finally {
        creatingDefaults.value = false
      }
    }
    
    const runScraper = async (source) => {
      try {
        runningScrapers.value.push(source.id)
        toast.info(`Starting scraper for ${source.name}...`)
        
        const response = await api.post(`/data-sources/${source.id}/run_now/`)
        
        toast.success(`Scraper completed! Found ${response.data.items_found} events (${response.data.new_items} new)`)
        
        await loadDataSources()
        await loadEvents()
        await loadRunHistory()
      } catch (error) {
        console.error('Error running scraper:', error)
        toast.error(error.response?.data?.error || 'Failed to run scraper')
      } finally {
        runningScrapers.value = runningScrapers.value.filter(id => id !== source.id)
      }
    }
    
    const pauseSource = async (source) => {
      try {
        await api.post(`/data-sources/${source.id}/pause/`)
        toast.info(`${source.name} paused`)
        await loadDataSources()
      } catch (error) {
        toast.error('Failed to pause source')
      }
    }
    
    const resumeSource = async (source) => {
      try {
        await api.post(`/data-sources/${source.id}/resume/`)
        toast.success(`${source.name} resumed`)
        await loadDataSources()
      } catch (error) {
        toast.error('Failed to resume source')
      }
    }
    
    const linkToAnalysis = async (event) => {
      try {
        await api.post(`/policy-events/${event.id}/link_to_analysis/`)
        event.linked_to_analysis = true
        toast.success('Event linked to analysis')
      } catch (error) {
        toast.error('Failed to link event')
      }
    }
    
    const debouncedSearch = debounce(() => {
      loadEvents()
    }, 300)
    
    // Utility functions
    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    }
    
    const formatSchedule = (schedule) => {
      const schedules = {
        'hourly': 'Hourly',
        'daily': 'Daily',
        'weekly': 'Weekly',
        'manual': 'Manual'
      }
      return schedules[schedule] || schedule
    }
    
    const formatDuration = (seconds) => {
      if (seconds < 60) return `${Math.round(seconds)}s`
      return `${Math.round(seconds / 60)}m`
    }
    
    const getStatusIcon = (status) => {
      const icons = {
        'ACTIVE': 'fas fa-check-circle',
        'PAUSED': 'fas fa-pause-circle',
        'ERROR': 'fas fa-exclamation-circle',
        'PENDING': 'fas fa-clock',
        'RUNNING': 'fas fa-spinner fa-spin'
      }
      return icons[status] || 'fas fa-question-circle'
    }
    
    const getRunStatusIcon = (status) => {
      const icons = {
        'completed': 'fas fa-check-circle',
        'failed': 'fas fa-times-circle',
        'started': 'fas fa-spinner fa-spin'
      }
      return icons[status] || 'fas fa-circle'
    }
    
    // Lifecycle
    onMounted(async () => {
      await loadDataSources()
      await loadEvents()
      await loadEventsByState()
      await loadRunHistory()
      await loadReadinessData()
    })
    
    return {
      // State
      loading,
      loadingEvents,
      loadingByState,
      loadingHistory,
      loadingReadiness,
      creatingDefaults,
      auditing,
      dataSources,
      policyEvents,
      eventsByState,
      runHistory,
      runningScrapers,
      openVariances,
      recurringRisks,
      selectedEvent,
      showCreateModal,
      activeTab,
      pilotStates,
      eventFilters,
      
      // Computed
      tabs,
      hasDataSources,
      
      // Methods
      loadDataSources,
      loadEvents,
      loadEventsByState,
      loadRunHistory,
      loadReadinessData,
      runVarianceAudit,
      createDefaultSources,
      runScraper,
      pauseSource,
      resumeSource,
      linkToAnalysis,
      debouncedSearch,
      
      // Utilities
      formatDate,
      formatSchedule,
      formatDuration,
      getStatusIcon,
      getRunStatusIcon
    }
  }
}
</script>

<style scoped>
.data-source-monitor {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Header */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #e9ecef;
}

.page-title {
  color: #1a365d;
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.page-title i {
  color: #4299e1;
}

.page-description {
  color: #64748b;
  font-size: 1rem;
  max-width: 600px;
  line-height: 1.6;
}

/* Pilot Banner */
.pilot-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
}

.pilot-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f59e0b;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
}

.pilot-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #92400e;
}

.state-tags {
  display: flex;
  gap: 0.5rem;
}

.state-tag {
  background: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-weight: 500;
  color: #92400e;
}

/* Tabs */
.tab-navigation {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: none;
  border: none;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
}

.tab-button:hover {
  color: #4299e1;
}

.tab-button.active {
  color: #4299e1;
  border-bottom-color: #4299e1;
}

.tab-count {
  background: #e2e8f0;
  color: #64748b;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
}

.tab-button.active .tab-count {
  background: #4299e1;
  color: white;
}

/* Source Cards */
.sources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.source-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;
}

.source-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.source-card.running {
  border-color: #4299e1;
  background: linear-gradient(135deg, #ebf8ff 0%, #ffffff 100%);
}

.source-card.error {
  border-color: #f56565;
  background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
}

.source-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.source-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
}

.source-info {
  flex: 1;
}

.source-info h4 {
  margin: 0 0 0.25rem 0;
  color: #1a365d;
  font-weight: 600;
}

.source-description {
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
}

.source-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.source-status.active {
  background: #c6f6d5;
  color: #22543d;
}

.source-status.paused {
  background: #e2e8f0;
  color: #64748b;
}

.source-status.error {
  background: #fed7d7;
  color: #c53030;
}

.source-status.running {
  background: #bee3f8;
  color: #2b6cb0;
}

.source-states {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.source-states .label {
  color: #64748b;
  font-size: 0.875rem;
}

.state-chips {
  display: flex;
  gap: 0.5rem;
}

.state-chip {
  background: #edf2f7;
  color: #4a5568;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.source-stats {
  display: flex;
  gap: 2rem;
  padding: 1rem 0;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 1rem;
}

.stat {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a365d;
}

.stat-label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
}

.source-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.last-run {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.875rem;
}

.source-actions {
  display: flex;
  gap: 0.5rem;
}

/* Events */
.events-filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-group label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 600;
}

.filter-group .form-select,
.filter-group .form-control {
  min-width: 180px;
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
}

.event-card:hover {
  border-color: #4299e1;
  box-shadow: 0 2px 8px rgba(66, 153, 225, 0.15);
}

.event-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.event-state {
  background: #1a365d;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.75rem;
}

.event-type {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.event-type.spa {
  background: #c6f6d5;
  color: #22543d;
}

.event-type.waiver_1115 {
  background: #bee3f8;
  color: #2b6cb0;
}

.event-type.waiver_1915 {
  background: #e9d8fd;
  color: #553c9a;
}

.event-date {
  margin-left: auto;
  color: #64748b;
  font-size: 0.875rem;
}

.event-title {
  margin: 0 0 0.5rem 0;
  color: #1a365d;
  font-size: 1rem;
  font-weight: 600;
}

.event-summary {
  margin: 0 0 0.75rem 0;
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
}

.event-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.event-status {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #e2e8f0;
  color: #64748b;
}

.event-status.approved {
  background: #c6f6d5;
  color: #22543d;
}

.event-status.pending {
  background: #fef3c7;
  color: #92400e;
}

.event-link {
  color: #4299e1;
  font-size: 0.875rem;
  text-decoration: none;
}

.event-link:hover {
  text-decoration: underline;
}

/* State Panels */
.state-panels {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.state-panel {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.state-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #1a365d 0%, #2c5282 100%);
  color: white;
}

.state-header h3 {
  margin: 0;
  font-size: 1.125rem;
}

.state-code {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  margin-right: 0.5rem;
}

.event-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.state-events {
  padding: 1rem;
}

.mini-event {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.mini-event:hover {
  background: #f7fafc;
}

.mini-type {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
}

.mini-title {
  flex: 1;
  font-size: 0.875rem;
  color: #1a365d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mini-date {
  color: #64748b;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.no-events {
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
  padding: 2rem;
}

/* History */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.history-item {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.history-item.completed {
  border-left: 4px solid #48bb78;
}

.history-item.failed {
  border-left: 4px solid #f56565;
}

.history-item.started {
  border-left: 4px solid #4299e1;
}

.history-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.history-item.completed .history-icon {
  background: #c6f6d5;
  color: #22543d;
}

.history-item.failed .history-icon {
  background: #fed7d7;
  color: #c53030;
}

.history-item.started .history-icon {
  background: #bee3f8;
  color: #2b6cb0;
}

.history-content {
  flex: 1;
}

.history-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.history-time {
  color: #64748b;
  font-size: 0.875rem;
}

.history-stats {
  color: #22543d;
}

.history-error {
  color: #c53030;
  font-size: 0.875rem;
}

.history-running {
  color: #2b6cb0;
}

.history-meta {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
}

.trigger-type {
  text-transform: capitalize;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.event-badges {
  display: flex;
  gap: 0.5rem;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.state-badge {
  background: #1a365d;
  color: white;
}

.type-badge.spa {
  background: #c6f6d5;
  color: #22543d;
}

.type-badge.waiver_1115 {
  background: #bee3f8;
  color: #2b6cb0;
}

.status-badge {
  background: #e2e8f0;
  color: #64748b;
}

.status-badge.approved {
  background: #c6f6d5;
  color: #22543d;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #64748b;
  cursor: pointer;
}

.modal-body {
  padding: 1.5rem;
}

.event-detail-title {
  margin: 0 0 1.5rem 0;
  color: #1a365d;
  font-size: 1.5rem;
}

.detail-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.detail-row label {
  font-weight: 600;
  color: #64748b;
}

.detail-section {
  margin-bottom: 1.5rem;
}

.detail-section h4 {
  margin: 0 0 0.5rem 0;
  color: #1a365d;
  font-size: 0.875rem;
  text-transform: uppercase;
}

.detail-section p {
  margin: 0;
  color: #4a5568;
  line-height: 1.6;
}

.detail-section.highlight {
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 1rem;
}

.detail-section.highlight h4 {
  color: #92400e;
}

.detail-actions {
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: #64748b;
}

.loading-state i,
.empty-state i {
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.empty-state h3 {
  margin: 0 0 0.5rem 0;
  color: #4a5568;
}

.empty-state p {
  margin: 0 0 1.5rem 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
}

.btn-outline-primary {
  background: white;
  color: #4299e1;
  border: 1px solid #4299e1;
}

.btn-outline-primary:hover {
  background: #ebf8ff;
}

.btn-outline-secondary {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-outline-secondary:hover {
  background: #f7fafc;
}

.btn-outline-success {
  background: white;
  color: #48bb78;
  border: 1px solid #48bb78;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Form Controls */
.form-select,
.form-control {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
}

.form-select:focus,
.form-control:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
}

/* Readiness Tab */
.readiness-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.readiness-stats {
  display: flex;
  gap: 2rem;
}

.readiness-stat {
  display: flex;
  flex-direction: column;
}

.readiness-stat .label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 600;
}

.readiness-stat .value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a365d;
}

.readiness-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 2rem;
}

.readiness-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.readiness-card .card-header {
  padding: 1.25rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.readiness-card .card-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #1e293b;
}

.readiness-card .card-header i {
  color: #3b82f6;
}

.readiness-card .card-body {
  padding: 1.5rem;
}

.readiness-table {
  width: 100%;
  border-collapse: collapse;
}

.readiness-table th {
  text-align: left;
  padding: 0.75rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #64748b;
  border-bottom: 2px solid #f1f5f9;
}

.readiness-table td {
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.875rem;
}

.impact-cell {
  font-weight: 600;
}

.impact-cell.high { color: #ef4444; }
.impact-cell.medium { color: #f59e0b; }
.impact-cell.low { color: #10b981; }

.risk-item {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.risk-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.risk-badge {
  font-size: 0.7rem;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 700;
}

.risk-badge.critical { background: #fee2e2; color: #991b1b; }
.risk-badge.warning { background: #fef3c7; color: #92400e; }

.risk-meta {
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.loading-inline, .empty-inline {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 1rem;
  }
  
  .pilot-banner {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .sources-grid {
    grid-template-columns: 1fr;
  }
  
  .state-panels {
    grid-template-columns: 1fr;
  }
  
  .events-filters {
    flex-direction: column;
  }
  
  .filter-group .form-select,
  .filter-group .form-control {
    min-width: 100%;
  }
}
</style>

